import React, { useCallback, useMemo } from 'react';

import { getReportDetails } from '../../../../store/reportDetails';
import { useAppSelector } from '../../../../store/configureStore';
import { NewAnomalyItem, Column, PCNLItemModel, PCNLAnomalyItem } from '../../../../utils/models';
import { getChunkedData, getItemsChunkedData, getPCNLAnomalyStyle, matchPhrases } from '../../../../utils/utilities';
import { TABLE_ITEM_COUNT_LIMITS } from '../../../../utils/constants';

import AnomalyRemarks from '../../custom-visual-inspection/report-items/anomalyRemarks';
import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import classNames from './style.module.scss';

interface Props {
	pcnlItems: PCNLItemModel[];
}

const PCNLSummary: React.FC<Props> = ({ pcnlItems }) => {
	const { itemCountPerPage } = useAppSelector(getReportDetails);

	const getHasSummaryData = useCallback((pcnlItem: PCNLItemModel) => {
		return matchPhrases(pcnlItem.hasUTMData, 'yes') 
				&& matchPhrases(pcnlItem.hasMPTData, 'yes')
				&& matchPhrases(pcnlItem.hasLPTData, 'yes')
				&& matchPhrases(pcnlItem.hasConcreteData, 'yes');
	}, []);

	const chunkData = useCallback((pcnlItem: PCNLItemModel) => {
		const hasSummaryData = getHasSummaryData(pcnlItem);
		if (hasSummaryData) {
			const chunkedData = getItemsChunkedData<PCNLAnomalyItem>(pcnlItem.anomalyItems, TABLE_ITEM_COUNT_LIMITS.PCNL_RISK_ASSESSMENT, itemCountPerPage);
			
			return { 
				displaySummaryData: hasSummaryData,
				chunkedData
			}
		}

		return { 
			displaySummaryData: hasSummaryData,
			chunkedData: getChunkedData(pcnlItem.anomalyItems, itemCountPerPage),
		}
	}, [itemCountPerPage, getHasSummaryData]);

	const items = useMemo(() => {
		return pcnlItems.filter((item) => !matchPhrases(item.pcnlItemName, 'risk assessment'))
	}, [pcnlItems])

	return (
		<>
			{items.map((pcnlItem) => {
				const { displaySummaryData, chunkedData } = chunkData(pcnlItem);

				return (
					<React.Fragment key={pcnlItem.pcnlItemName}>
						<Conditional isVisible={pcnlItem.anomalyItems.length > 0}>
							{chunkedData.map((items, index) => (
								<CustomSection
									key={index}
									sectionTitle="Summary"
									sectionSubtitle={pcnlItem.pcnlItemName}
								>
									<div className="p-1">
										<PDFTablePage data={items} columns={columns} />

										<Conditional isVisible={displaySummaryData && index === 0}>
											<Conditional isVisible={matchPhrases(pcnlItem.hasUTMData, 'yes')}>
												<div className="mt-2">
													<div className={classNames.pcnlSummaryHeader}>
														UTM SUMMARY
													</div>
													<div className={classNames.pcnlSummaryBody}>
														{pcnlItem.utmData.overview.summary}
													</div>
												</div>
											</Conditional>

											<Conditional isVisible={matchPhrases(pcnlItem.hasMPTData, 'yes')}>
												<div className="mt-2">
													<div className={classNames.pcnlSummaryHeader}>
														MPT SUMMARY
													</div>
													<div className={classNames.pcnlSummaryBody}>
														{pcnlItem.mptData.summary}
													</div>
												</div>
											</Conditional>

											<Conditional isVisible={matchPhrases(pcnlItem.hasLPTData, 'yes')}>
												<div className="mt-2">
													<div className={classNames.pcnlSummaryHeader}>
														LPT SUMMARY
													</div>
													<div className={classNames.pcnlSummaryBody}>
														{pcnlItem.lptData.summary}
													</div>
												</div>
											</Conditional>

											<Conditional isVisible={matchPhrases(pcnlItem.hasConcreteData, 'yes')}>
												<div className="mt-2">
													<div className={classNames.pcnlSummaryHeader}>
														CONCRETE HAMMER REPORT SUMMARY
													</div>
													<div className={classNames.pcnlSummaryBody}>
														{pcnlItem.concreteHammerData.overview.summary}
													</div>
												</div>
											</Conditional>
										</Conditional>
									</div>
								</CustomSection>
							))}
						</Conditional>

						<Conditional isVisible={pcnlItem.anomalyItems.length === 0}>
							<CustomSection sectionTitle="Summary" sectionSubtitle={pcnlItem.pcnlItemName}>
								<div className="p-1">
									<PDFTablePage data={[]} columns={columns} />

									<div className="mt-2">
										<div className={classNames.pcnlSummaryHeader}>
											UTM SUMMARY
										</div>
										<div className={classNames.pcnlSummaryBody}>
											{pcnlItem.utmData.overview.summary}
										</div>
									</div>

									<div className="mt-2">
										<div className={classNames.pcnlSummaryHeader}>
											MPT SUMMARY
										</div>
										<div className={classNames.pcnlSummaryBody}>
											{pcnlItem.mptData.summary}
										</div>
									</div>

									<div className="mt-2">
										<div className={classNames.pcnlSummaryHeader}>
											LPT SUMMARY
										</div>
										<div className={classNames.pcnlSummaryBody}>
											{pcnlItem.lptData.summary}
										</div>
									</div>

									<div className="mt-2">
										<div className={classNames.pcnlSummaryHeader}>
											CONCRETE HAMMER REPORT SUMMARY
										</div>
										<div className={classNames.pcnlSummaryBody}>
											{pcnlItem.concreteHammerData.overview.summary}
										</div>
									</div>
								</div>
							</CustomSection>
						</Conditional>
					</React.Fragment>
				);
			})}
		</>
	);
};

const columns: Column[] = [
	{
		path: 'description',
		label: 'Anomaly Description',
		content: (item: NewAnomalyItem) => (
			<span>{item.description.map(({ value }) => value).join('. ')}</span>
		),
		style: (item: NewAnomalyItem) => ({ textAlign: 'left' }),
	},
	{
		path: 'recommendation',
		label: 'Recommendation',
		content: (item: NewAnomalyItem) => <AnomalyRemarks anomaly={item} />,
		style: (item: NewAnomalyItem) => ({ textAlign: 'left' }),
	},
	{
		path: 'rootCause',
		label: 'Root Cause',
	},
	{
		path: 'interventionRequired',
		label: 'Intervention Required',
	},
	{
		path: 'riskFactor',
		label: 'Risk Factor',
		style: (item: PCNLAnomalyItem) => getPCNLAnomalyStyle(item),
	},
	{
		path: 'images',
		label: 'Anomaly Photo Ref.',
		content: (item: NewAnomalyItem) => <span>{item.id}</span>,
	},
];

export default PCNLSummary;
